
import { defineComponent, PropType, ref, computed, onMounted, onUnmounted, watch } from 'vue'
import { useRoute } from 'vue-router'
import { isXpClient } from '@/utils/xpClient'
import moment from 'moment'
import Nodata from '@/components/page/nodata.vue'
import HeaderSettingsPanel from './HeaderSettingsPanel.vue'
import DragonSignalBacktest from './DragonSignalBacktest.vue'
import DragonHunterBacktest from './DragonHunterBacktest.vue'
import QuantGoldBacktest from './QuantGoldBacktest.vue'
import { getTableColumns, TableColumn, getCourseName, getHotCasesColumns, getCourseConfig } from './courseConfig'
import { formatNumber, formatPercent, formatAmount, maskStockName, maskStockCode, isMaskEnabled, isEtfStock, formatPrice } from './tableColumns'
import { getChipStateColor, getChipShapeColor, getInPoolTimeColor, formatInPoolTime } from './chipMasterUtils'
import { formatMengzhuDate, getChangeColor } from './mengzhuTrendUtils'
import {
  filterAgencyColumns,
  formatAgencyRate,
  getAgencyGoldColumnTitle,
  getAgencyRateColor,
  openAgencyIndicatorNavigation,
  resolveAgencyDisplayLastPx
} from './agencyIntradayUtils'
import {
  generateStorageKey,
  saveColumnOrder,
  getColumnOrder,
  clearColumnOrder,
  validateColumnConfig,
  filterDraggableColumns,
  applyColumnOrder,
  generateColumnConfigVersion,
  ListType,
  SignalType
} from './columnOrderService'

interface PoolData {
  symbol: string
  market: string
  name: string
  [key: string]: any
}

export default defineComponent({
  name: 'PoolTable',
  components: { Nodata, HeaderSettingsPanel, DragonSignalBacktest, DragonHunterBacktest, QuantGoldBacktest },
  props: {
    dataSource: {
      type: Array as PropType<PoolData[]>,
      default: () => []
    },
    loading: {
      type: Boolean,
      default: false
    },
    hasMore: {
      type: Boolean,
      default: true
    },
    loadingMore: {
      type: Boolean,
      default: false
    },
    theme: {
      type: String,
      default: 'light'
    },
    courseNo: {
      type: String,
      default: 'e4408ecedc054856946546a185e8c047'
    },
    productData: {
      type: Array as PropType<any[]>,
      default: () => []
    },
    isClick: {
      type: Number,
      default: 2  // 1: 弹窗模式(只有双击) 2: 内嵌模式(单击+双击)
    },
    defaultSortField: {
      type: String,
      default: 'pxChangRate'
    },
    defaultSortOrder: {
      type: String,
      default: 'descend'
    },
    // 当前排序字段（受控模式）
    sortFieldProp: {
      type: String,
      default: ''
    },
    // 当前排序顺序（受控模式）
    sortOrderProp: {
      type: String,
      default: ''
    },
    // 盟主趋势顶级 Tab
    mengzhuMainTab: {
      type: String,
      default: 'signalPool'
    },
    // 盟主趋势：最后一个有效交易日（用于判断最高涨幅和累计涨幅的显示逻辑）
    mengzhuLastTradingDay: {
      type: String,
      default: ''
    },
    // 盟主趋势：信号池更新时间
    mengzhuUpdateTime: {
      type: String,
      default: ''
    },
    // 盟主趋势：信号类型（latest 或 history）
    mengzhuSignalType: {
      type: String,
      default: 'latest'
    },
    // 龙头信号
    isDragonSignal: {
      type: Boolean,
      default: false
    },
    dragonSignalStrategyCode: {
      type: String,
      default: 'dragon_signal_sprint'
    },
    // 龙头猎手
    isDragonHunter: {
      type: Boolean,
      default: false
    },
    dragonHunterMainTab: {
      type: String,
      default: 'newChose'
    },
    dragonHunterSubTab: {
      type: String,
      default: 'dragonStar'
    },
    isQuantGold: { type: Boolean, default: false },
    quantGoldMainTab: { type: String, default: 'newChose' },
    quantGoldSubTab: { type: String, default: 'entrySignal' },
    isAgencyIntraday: { type: Boolean, default: false },
    agencyMainTab: { type: String, default: 'allMarket' },
    agencySubTab: { type: String, default: 'agency' },
    agencyHasQuantPermission: { type: Boolean, default: false }
  },
  emits: ['change', 'row-click', 'row-dblclick', 'load-more', 'visible-range-change', 'open-ai-interp', 'agency-gold-dblclick'],
  setup(props, { emit, expose }) {
    const route = useRoute()
    const tableWrapperRef = ref<HTMLElement | null>(null)
    const tableBodyRef = ref<HTMLElement | null>(null)
    const tableHeaderRef = ref<HTMLElement | null>(null)
    // 设置图标 hover 状态
    const settingsIconHover = ref(false)
    const isXpOs = computed(() => isXpClient(route.query as Record<string, unknown>))
    const settingsIconSrc = computed(() => {
      if (props.theme === 'dark') {
        return settingsIconHover.value ? require('@/assets/baiseactive1.png') : require('@/assets/heiseblack.png')
      }
      return settingsIconHover.value ? require('@/assets/baiseactive1.png') : require('@/assets/heiseblack.png')
    })
    const agencyGoldLockSrc = require('@/assets/lock.png')
    // 初始化时使用默认排序，如果传入了受控 props 则优先使用
    const sortField = ref<string>(props.sortFieldProp || props.defaultSortField)
    const sortOrder = ref<string>(props.sortOrderProp || props.defaultSortOrder)
 
    // 监听受控 props 变化
    watch(() => props.sortFieldProp, (newVal) => { 
      // 如果 props 传了值，同步到内部 ref
      if (newVal !== undefined) {
        sortField.value = newVal
      }
    })
    watch(() => props.sortOrderProp, (newVal) => {
      // 如果 props 传了值，同步到内部 ref
      if (newVal !== undefined) {
        sortOrder.value = newVal === 'asc' ? 'ascend' : (newVal === 'desc' ? 'descend' : newVal)
      }
    })

    // 监听父组件的重置方法调用，直接覆盖内部状态，确保不触发默认恢复
    const resetSort = (newField?: string, newOrder?: string) => {
      if (newField !== undefined) {
        sortField.value = newField
      } else {
        sortField.value = props.defaultSortField
      }

      if (newOrder !== undefined) {
        sortOrder.value = newOrder === 'asc' ? 'ascend' : (newOrder === 'desc' ? 'descend' : newOrder)
      } else {
        sortOrder.value = props.defaultSortOrder
      }
    }

    const getScrollTop = () => tableBodyRef.value?.scrollTop ?? 0
    const setScrollTop = (top: number) => {
      if (tableBodyRef.value) {
        tableBodyRef.value.scrollTop = top
      }
    }

    // ==================== 可视区域 WebSocket 订阅（scrollTop + 缓冲，全信号池统一） ====================
    const VISIBLE_ROW_BUFFER_PX = 600
    const VISIBLE_EMIT_DEBOUNCE_MS = 50
    const VISIBLE_SCROLL_END_MS = 80
    const VISIBLE_EMIT_MAX_RETRY = 8
    const VISIBLE_EMIT_RETRY_MS = 120
    const VISIBLE_FALLBACK_MAX = 50

    let scrollVisibleTimer: ReturnType<typeof setTimeout> | null = null
    let scrollEndTimer: ReturnType<typeof setTimeout> | null = null
    let scrollRoot: HTMLElement | Window | null = null
    let scrollHandler: (() => void) | null = null
    let visibleEmitRetryTimer: ReturnType<typeof setTimeout> | null = null
    let visibleEmitRetryCount = 0
    let lastVisibleEmitSignature = ''

    const getVisibleRowKey = (item: { symbol?: string; market?: string }) => {
      const symbol = String(item.symbol ?? '')
      const market = String(item.market || 'sz').toLowerCase()
      return `${symbol}_${market}`
    }

    const getScrollRoot = (container: HTMLElement): HTMLElement | null => {
      let el: HTMLElement | null = container
      while (el) {
        const { overflowY } = window.getComputedStyle(el)
        const canScroll = overflowY === 'auto' || overflowY === 'scroll' || overflowY === 'overlay'
        if (canScroll && el.scrollHeight > el.clientHeight + 1) {
          return el
        }
        el = el.parentElement
      }
      return null
    }

    const appendVisibleRow = (
      el: HTMLElement,
      visible: PoolData[],
      seen: Set<string>
    ) => {
      const symbol = el.dataset.symbol
      if (!symbol) return
      const record = props.dataSource.find((item: PoolData) => String(item.symbol) === symbol)
      if (!record) return
      const key = getVisibleRowKey(record)
      if (seen.has(key)) return
      seen.add(key)
      visible.push(record)
    }

    const collectRowsFromDom = (rows: NodeListOf<Element>, seen: Set<string>, visible: PoolData[]) => {
      rows.forEach((row) => appendVisibleRow(row as HTMLElement, visible, seen))
    }

    const collectVisibleRowsByScroll = (): PoolData[] => {
      const container = tableBodyRef.value
      if (!container) return []

      const visible: PoolData[] = []
      const seen = new Set<string>()
      const rows = container.querySelectorAll('tr[data-symbol]')
      if (!rows.length) return []

      const root = getScrollRoot(container) || container
      const rootRect = root.getBoundingClientRect()
      const visibleTop = rootRect.top - VISIBLE_ROW_BUFFER_PX
      const visibleBottom = rootRect.bottom + VISIBLE_ROW_BUFFER_PX

      rows.forEach((row) => {
        const el = row as HTMLElement
        const rowRect = el.getBoundingClientRect()
        if (rowRect.bottom < visibleTop || rowRect.top > visibleBottom) return
        appendVisibleRow(el, visible, seen)
      })

      // 部分嵌入浏览器 getBoundingClientRect 异常：有 DOM 行但可视区计算为 0
      if (visible.length === 0) {
        collectRowsFromDom(rows, seen, visible)
      }

      return visible
    }

    const collectVisibleRowsFallback = (): PoolData[] => {
      const container = tableBodyRef.value
      if (!container || !props.dataSource.length) return []

      const rows = container.querySelectorAll('tr[data-symbol]')
      const visible: PoolData[] = []
      const seen = new Set<string>()
      if (rows.length > 0) {
        collectRowsFromDom(rows, seen, visible)
      }
      if (visible.length > 0) return visible

      if (props.dataSource.length <= VISIBLE_FALLBACK_MAX) {
        return props.dataSource.slice()
      }
      return props.dataSource.slice(0, VISIBLE_FALLBACK_MAX)
    }

    const unbindScrollListener = () => {
      if (scrollRoot && scrollHandler) {
        scrollRoot.removeEventListener('scroll', scrollHandler)
      }
      scrollRoot = null
      scrollHandler = null
    }

    const bindScrollListener = () => {
      unbindScrollListener()
      const container = tableBodyRef.value
      if (!container) return

      scrollHandler = () => scheduleScrollVisibleEmit()
      const root = getScrollRoot(container)
      if (root) {
        scrollRoot = root
        try {
          root.addEventListener('scroll', scrollHandler, { passive: true })
        } catch {
          root.addEventListener('scroll', scrollHandler)
        }
      } else {
        scrollRoot = window
        try {
          window.addEventListener('scroll', scrollHandler, { passive: true })
        } catch {
          window.addEventListener('scroll', scrollHandler)
        }
      }
    }

    const emitVisibleRangeNow = (fromUserScroll = false) => {
      let visibleData = collectVisibleRowsByScroll()
      if (visibleData.length === 0 && props.dataSource.length > 0) {
        if (visibleEmitRetryCount < VISIBLE_EMIT_MAX_RETRY) {
          visibleEmitRetryCount += 1
          if (visibleEmitRetryTimer) clearTimeout(visibleEmitRetryTimer)
          visibleEmitRetryTimer = setTimeout(() => {
            visibleEmitRetryTimer = null
            emitVisibleRangeNow(fromUserScroll)
          }, VISIBLE_EMIT_RETRY_MS)
          return
        }
        visibleData = collectVisibleRowsFallback()
      }
      if (visibleData.length === 0) return
      visibleEmitRetryCount = 0
      const signature = visibleData.map((item) => getVisibleRowKey(item)).sort().join(',')
      if (signature === lastVisibleEmitSignature) return
      lastVisibleEmitSignature = signature
      emit('visible-range-change', visibleData, { fromUserScroll })
    }

    const scheduleScrollVisibleEmit = () => {
      if (scrollVisibleTimer) {
        clearTimeout(scrollVisibleTimer)
      }
      scrollVisibleTimer = setTimeout(() => {
        scrollVisibleTimer = null
        emitVisibleRangeNow(true)
      }, VISIBLE_EMIT_DEBOUNCE_MS)

      // 快速滚动时 trailing debounce 会滞后，scroll 停止后立即同步一次最终可视区
      if (scrollEndTimer) {
        clearTimeout(scrollEndTimer)
      }
      scrollEndTimer = setTimeout(() => {
        scrollEndTimer = null
        if (scrollVisibleTimer) {
          clearTimeout(scrollVisibleTimer)
          scrollVisibleTimer = null
        }
        emitVisibleRangeNow(true)
      }, VISIBLE_SCROLL_END_MS)
    }

    const refreshVisibleRangeSubscription = () => {
      if (scrollVisibleTimer) {
        clearTimeout(scrollVisibleTimer)
        scrollVisibleTimer = null
      }
      if (scrollEndTimer) {
        clearTimeout(scrollEndTimer)
        scrollEndTimer = null
      }
      lastVisibleEmitSignature = ''
      setTimeout(() => {
        bindScrollListener()
        emitVisibleRangeNow()
      }, 150)
    }

    // 暴露给父组件的方法
    const emitVisibleRange = () => {
      lastVisibleEmitSignature = ''
      emitVisibleRangeNow()
    }

    // 判断是否启用敏感信息打码
    const shouldMask = computed(() => isMaskEnabled(route.query.mcnMode as string))

    // 表格容器样式：只有 os=px 时才设置固定高度
    const tableContainerStyle = computed(() => {
      if (isXpClient(route.query as Record<string, unknown>)) {
        return { height: 'calc(100vh - 192px)' }
      }
      return {}
    })

    // 获取显示的股票名称（根据打码模式）
    const getDisplayName = (name: string) => {
      return shouldMask.value ? maskStockName(name) : name
    }

    // 获取显示的股票代码（根据打码模式）
    const getDisplayCode = (code: string) => {
      return shouldMask.value ? maskStockCode(code) : code
    }

    // 暴露方法给父组件
    expose({ resetSort, emitVisibleRange, getScrollTop, setScrollTop })

    // 判断是否为盟主趋势（需要在 columns 之前定义，因为 columns 依赖它）
    const isMengzhuTrend = computed(() => props.courseNo === '399a9139a99a4300a9bf004b9949eb9f')

    // 判断是否为箱体擒龙
    const isBoxDragon = computed(() => props.courseNo === '33130259066e4650bd0b7cf6b23d9488')

    // 判断是否为龙头信号
    const isDragonSignal = computed(() => props.courseNo === 'b20f2d6e654948ae8c99bf650cbbb0e2')

    // 判断是否为龙头猎手
    const isDragonHunter = computed(() => props.courseNo === '03fc903a5ac84ff6982e4b83a1f35c61')
    const isQuantGold = computed(() => props.courseNo === '5f0a51df3bf44351aa50b1df798c9836')
    const isAgencyIntraday = computed(() => props.isAgencyIntraday || props.courseNo === '1c261467f05747f2beb3115d26a2b98c')

    // 格式化个股热度（龙头信号特有）
    const formatStockHot = (val: number | null | undefined) => {
      if (val === null || val === undefined) return '--'
      if (val >= 10000) return (val / 10000).toFixed(1) + '万'
      return String(val)
    }

    // 格式化龙头猎手热度字段（个股热度/板块热度，逻辑同成交额）
    const formatHunterHot = (val: number | null | undefined) => {
      if (val === null || val === undefined) return '--'
      if (val === 0) return '-'
      if (val >= 100000000) return (val / 100000000).toFixed(2) + '亿'
      if (val >= 10000) return (val / 10000).toFixed(1) + '万'
      return String(val)
    }

    // 龙头猎手入选类型颜色
    const getHunterSelectTypeColor = (selectType: number) => {
      if (selectType === 5) return 'rgb(255,0,0)'              // 龙头启动：红色
      if (selectType === 4 || selectType === 3) return 'rgb(225,60,255)'  // 龙头加强/后排超车：紫色
      if (selectType === 2 || selectType === 1) return 'rgb(255,125,0)'   // 二波右侧/二波左侧：橙色
      return ''
    }

    // 龙头猎手入选类型文本
    const getHunterSelectTypeText = (selectType: number) => {
      const map: Record<number, string> = { 5: '龙头启动', 4: '龙头加强', 3: '后排超车', 2: '二波右侧', 1: '二波左侧' }
      return map[selectType] || '--'
    }

    // 格式化龙头猎手日期

    const formatUpSpaceStar = (val: number | null | undefined) => {
      if (val === null || val === undefined) return '--'
      return val + '星'
    }
    const formatQuantGoldDate = (val: string | number | null | undefined) => {
      if (val === null || val === undefined || val === '' || val === 0 || val === '0') return '—'
      if (typeof val === 'number') {
        const ms = val < 1e12 ? val * 1000 : val
        return moment(ms).format('YYYY-MM-DD')
      }
      const str = String(val).trim()
      if (!str) return '—'
      if (/^\d{4}-\d{2}-\d{2}/.test(str)) return str.slice(0, 10)
      const n = Number(str)
      if (Number.isFinite(n) && n > 0) {
        const ms = n < 1e12 ? n * 1000 : n
        return moment(ms).format('YYYY-MM-DD')
      }
      return str.slice(0, 10)
    }
    const formatHunterDate = (val: number | null | undefined) => {
      if (!val) return '--'
      return moment(val).format('YYYY-MM-DD')
    }

    // 根据 courseNo 获取原始列配置
    const columns = computed(() => {
      // 盟主趋势近30天热门案例使用特定的列配置
      if (isMengzhuTrend.value && props.mengzhuMainTab === 'hotCases') {
        return getHotCasesColumns(props.courseNo)
      }
      // 龙头猎手往期入选使用专属列配置
      if (isDragonHunter.value && props.dragonHunterMainTab === 'beforeChose') {
        const config = getCourseConfig(props.courseNo)
        return config.beforeChoseColumns || getTableColumns(props.courseNo)
      }
      // 龙头猎手热门案例使用 hotCasesColumns
      if (isDragonHunter.value && props.dragonHunterMainTab === 'hotCase') {
        return getHotCasesColumns(props.courseNo)
      }
      if (isQuantGold.value) {
        const config = getCourseConfig(props.courseNo)
        if (props.quantGoldMainTab === 'hotCase') {
          return config.hotCasesColumns || getTableColumns(props.courseNo)
        }
        if (props.quantGoldSubTab === 'exitSignal') {
          return config.exitSignalColumns || getTableColumns(props.courseNo)
        }
        if (props.quantGoldSubTab === 'shortSignal') {
          return config.shortSignalColumns || getTableColumns(props.courseNo)
        }
        if (props.quantGoldSubTab === 'coverShortSignal') {
          return config.coverShortSignalColumns || getTableColumns(props.courseNo)
        }
        return getTableColumns(props.courseNo)
      }
      if (isAgencyIntraday.value) {
        const baseCols = getTableColumns(props.courseNo).map(col => {
          if (col.key === 'goldSignal') {
            return {
              ...col,
              title: getAgencyGoldColumnTitle(props.agencyHasQuantPermission),
              align: 'right' as const,
              // 淘金指可排序；量化信号（无权限）不可排序
              sortable: props.agencyHasQuantPermission
            }
          }
          return col
        })
        return filterAgencyColumns(
          baseCols,
          props.agencyMainTab as 'allMarket' | 'optional',
          props.agencySubTab as 'agency' | 'retail',
          props.agencyHasQuantPermission
        )
      }
      return getTableColumns(props.courseNo)
    })

    // 表头设置面板状态
    const headerSettingsVisible = ref(false)

    // 自定义列顺序（可拖动列的 key 数组）
    const customColumnOrder = ref<string[]>([])

    // 获取当前列表类型
    const currentListType = computed((): ListType | string => {
      if (isMengzhuTrend.value && props.mengzhuMainTab === 'hotCases') {
        return 'hotCases'
      }
      if (isDragonHunter.value) {
        return props.dragonHunterMainTab
      }
      if (isQuantGold.value) {
        return props.quantGoldMainTab
      }
      if (isAgencyIntraday.value) {
        return props.agencyMainTab
      }
      if (isDragonSignal.value) {
        return props.dragonSignalStrategyCode
      }
      return 'signalPool'
    })

    // 获取当前信号类型
    const currentSignalType = computed((): SignalType | string => {
      if (isDragonHunter.value) {
        return props.dragonHunterSubTab
      }
      if (isQuantGold.value) {
        return props.quantGoldSubTab
      }
      if (isAgencyIntraday.value) {
        return props.agencySubTab
      }
      return props.mengzhuSignalType as SignalType || 'latest'
    })

    // 获取存储键
    const storageKey = computed(() => {
      // 兼容所有模式：将 courseNo、一级菜单、二级菜单组合为唯一键
      const isNeedSub = currentListType.value === 'signalPool' || isDragonSignal.value || (isDragonHunter.value && currentListType.value === 'newChose') || (isQuantGold.value && (currentListType.value === 'newChose' || currentListType.value === 'beforeChose')) || (isAgencyIntraday.value && currentListType.value === 'allMarket')
      return generateStorageKey(
        props.courseNo,
        currentListType.value as any,
        isNeedSub ? (currentSignalType.value as any) : undefined
      )
    })

    // 获取列配置版本
    const columnConfigVersion = computed(() => {
      return generateColumnConfigVersion(columns.value)
    })

    // 可拖动列（排除锁定列）
    const draggableColumns = computed(() => {
      const draggable = filterDraggableColumns(columns.value)
      // 如果有自定义顺序，按自定义顺序排列
      if (customColumnOrder.value.length > 0) {
        const orderedColumns: TableColumn[] = []
        const columnMap = new Map(draggable.map(col => [col.key, col]))
        for (const key of customColumnOrder.value) {
          const col = columnMap.get(key)
          if (col) {
            orderedColumns.push(col)
            columnMap.delete(key)
          }
        }
        // 添加剩余的列
        for (const col of columnMap.values()) {
          orderedColumns.push(col)
        }
        return orderedColumns
      }
      return draggable
    })

    // 默认可拖动列（用于恢复默认）
    const defaultDraggableColumns = computed(() => {
      return filterDraggableColumns(columns.value)
    })

    // 最终显示的列（锁定列 + 自定义顺序的可拖动列）
    const displayColumns = computed(() => {
      if (customColumnOrder.value.length > 0) {
        return applyColumnOrder(columns.value, customColumnOrder.value)
      }
      return columns.value
    })

    // 加载保存的列顺序
    const loadColumnOrder = () => {
      const saved = getColumnOrder(storageKey.value)
      if (saved) {
        // 验证配置是否兼容
        if (validateColumnConfig(saved.columnKeys, columns.value) && saved.version === columnConfigVersion.value) {
          customColumnOrder.value = saved.columnKeys
        } else {
          // 配置不兼容，清除旧设置
          clearColumnOrder(storageKey.value)
          customColumnOrder.value = []
        }
      } else {
        customColumnOrder.value = []
      }
    }

    // 打开表头设置面板
    const openHeaderSettings = () => {
      headerSettingsVisible.value = true
    }

    // 处理表头设置应用
    const handleHeaderSettingsApply = (newColumns: TableColumn[]) => {
      const newOrder = newColumns.map(col => col.key)
      customColumnOrder.value = newOrder
      // 保存到 localStorage
      saveColumnOrder(storageKey.value, newOrder, columnConfigVersion.value)
    }

    // 处理表头设置取消
    const handleHeaderSettingsCancel = () => {
      // 不做任何操作，保持原有顺序
    }

    // 监听存储键变化，重新加载列顺序
    watch(storageKey, () => {
      loadColumnOrder()
    })

    // 数据源变化后刷新可视区订阅
    watch(() => props.dataSource.length, () => {
      refreshVisibleRangeSubscription()
    })

    const scheduleVisibleRangeAfterDomReady = () => {
      setTimeout(() => {
        bindScrollListener()
        emitVisibleRangeNow()
      }, 150)
    }

    // Tab 切换导致表格 DOM 重建时恢复订阅
    watch(() => props.mengzhuSignalType, (newType, oldType) => {
      if (oldType === 'backtest' && newType !== 'backtest') {
        scheduleVisibleRangeAfterDomReady()
      }
    })

    watch(() => props.dragonHunterMainTab, (newTab, oldTab) => {
      if (oldTab === 'dataTrack' && newTab !== 'dataTrack') {
        scheduleVisibleRangeAfterDomReady()
      }
    })

    watch(() => props.quantGoldMainTab, (newTab, oldTab) => {
      if (oldTab === 'dataTrack' && newTab !== 'dataTrack') {
        scheduleVisibleRangeAfterDomReady()
      }
    })

    onMounted(() => {
      loadColumnOrder()
      bindScrollListener()
      setTimeout(() => emitVisibleRangeNow(), 100)
    })
    const courseName = computed(() => getCourseName(props.courseNo))

    // 盟主趋势顶级 Tab（用于模板中访问）
    const currentMengzhuMainTab = computed(() => props.mengzhuMainTab)

    // 获取对齐类名
    const getAlignClass = (align?: string) => {
      switch (align) {
        case 'left': return 'th-left'
        case 'center': return 'th-center'
        case 'right': return 'th-right'
        default: return 'th-left'
      }
    }

    // 获取固定列的 left 值
    const getFixedLeft = (currentIndex: number) => {
      let left = 0
      for (let i = 0; i < currentIndex; i++) {
        const col = displayColumns.value[i]
        if (col.fixed === 'left') {
          left += typeof col.width === 'number' ? col.width : parseInt(col.width as string, 10) || 0
        }
      }
      return left
    }

    // 格式化单元格值
    const formatCellValue = (val: any, formatter?: string, symbol?: string) => {
      switch (formatter) {
        case 'number': return formatPrice(val, symbol)
        case 'percent': return formatPercent(val)
        case 'amount': return formatAmount(val)
        default: return val ?? '--'
      }
    }

    // 格式化最新价：如果为0则显示--
    const formatLastPx = (lastPx: number | null | undefined, symbol?: string) => {
      if (lastPx === null || lastPx === undefined || lastPx === 0) {
        return '--'
      }
      return formatPrice(lastPx, symbol)
    }

    const formatAgencyLastPx = (record: PoolData) => {
      return formatLastPx(resolveAgencyDisplayLastPx(record as any), record.market)
    }

    // 格式化涨幅：最新价缺失时仍可展示涨幅（订阅推送场景）
    const formatPxChangRate = (lastPx: number | null | undefined, pxChangRate: number | null | undefined) => {
      if (pxChangRate === null || pxChangRate === undefined) {
        if (lastPx === null || lastPx === undefined || lastPx === 0) {
          return '--'
        }
      }
      if ((lastPx === null || lastPx === undefined || lastPx === 0) &&
        (pxChangRate === null || pxChangRate === undefined)) {
        return '--'
      }
      return formatPercent(pxChangRate)
    }

    // 格式化入选日涨跌幅：四舍五入取小数点后两位，为正不显示+号，为负显示-号
    const formatSelectRate = (val: number | null | undefined) => {
      if (val === null || val === undefined) {
        return '--'
      }
      return (val * 100).toFixed(2) + '%'
    }

    // 获取单元格样式类
    const getCellClass = () => {
      // 涨跌相关列不再使用CSS类，改用内联样式处理颜色
      return []
    }

    // 获取单元格样式
    const getCellStyle = (col: TableColumn, colIndex: number, record: PoolData) => {
      const style: Record<string, string> = {}
      if (col.fixed === 'left') {
        style.left = getFixedLeft(colIndex) + 'px'
      }
      // 固定颜色（从配置中读取，如金额列 #19B7E1，总量/换手列 #959EAD）
      if (col.color) {
        style.color = col.color
      }
      // 特殊列动态颜色
      if (col.key === 'macd') {
        style.color = getMacdColor(record.macd)
      }
      // 四维猎手的资金标尺使用动态颜色，盟主趋势/龙头信号/龙头猎手的成交额使用配置中的固定颜色
      if (col.key === 'amount' && !isMengzhuTrend.value && !isDragonSignal.value && !isDragonHunter.value && !isQuantGold.value) {
        style.color = getAmountColor(record.amount)
      }
      // 最高涨幅（四维猎手）：>0 红色，<0 绿色，=0 灰色
      if (col.key === 'highRate') {
        const val = record.highRate
        if (val === null || val === undefined || val === 0) {
          style.color = props.theme === 'dark' ? '#c8c8c8' : '#4d4d4d'
        } else if (val > 0) {
          style.color = props.theme === 'dark' ? '#ff5c5c' : '#cc2b1d'
        } else {
          // val < 0
          style.color = props.theme === 'dark' ? '#39e365' : '#439731'
        }
      }
      // 最高涨幅 / 量化淘金累计涨幅：>0 红色，<0 绿色，空值/0 灰色
      if (col.key === 'highPct' || (isQuantGold.value && col.key === 'cumRate')) {
        const val = col.key === 'highPct' ? record.highPct : record.cumRate
        if (val === null || val === undefined || val === 0) {
          style.color = props.theme === 'dark' ? '#c8c8c8' : '#4d4d4d'
        } else if (val > 0) {
          style.color = props.theme === 'dark' ? '#ff5c5c' : '#cc2b1d'
        } else {
          style.color = props.theme === 'dark' ? '#39e365' : '#439731'
        }
      }
      // 最新价、涨幅、入选价：根据涨幅值显示颜色
      // >0: 红色; <0: 绿色; =0: 灰色
      // 盟主趋势的入选价使用代码列颜色，不使用涨跌色
      // 箱体擒龙的入选价(selectedPrice)使用名称列颜色，和四维猎手的名称列保持一致
      if (['lastPx', 'pxChangRate', 'selectedPx', 'closePx', 'selectedPrice', 'selectRate'].includes(col.key)) {
        // 四维猎手的入选日涨跌幅(selectRate)使用自身的涨跌值判断颜色，而不是当前涨幅
        if (col.key === 'selectRate') {
          const rate = record.selectRate
          if (rate === null || rate === undefined || rate === 0) {
            style.color = props.theme === 'dark' ? '#c8c8c8' : '#4d4d4d'
          } else if (rate > 0) {
            style.color = props.theme === 'dark' ? '#ff5c5c' : '#cc2b1d'
          } else {
            style.color = props.theme === 'dark' ? '#39e365' : '#439731'
          }
        } else if (isBoxDragon.value && (col.key === 'closePx' || col.key === 'selectedPrice')) {
          // 箱体擒龙的入选价使用名称列颜色（和四维猎手的名称列一致）
          style.color = props.theme === 'dark' ? '#DFE2EA' : '#333'
        } else if (isDragonSignal.value && col.key === 'selectedPrice') {
          // 龙头信号的入选价使用代码列颜色（和四维猎手的代码列一致）
          style.color = props.theme === 'dark' ? '#DFE2EA' : '#4D4D4D'
        } else if (col.key === 'closePx' && (record.closePx === null || record.closePx === undefined)) {
          // 四维猎手入选价无值（显示 --）时用默认色，不跟涨幅变色
          style.color = props.theme === 'dark' ? '#c8c8c8' : '#4d4d4d'
        } else {
          // 盟主趋势的入选价(buyPx)不在这里处理，使用代码列颜色
          const rate = record.pxChangRate
          if (rate === null || rate === undefined || rate === 0) {
            style.color = props.theme === 'dark' ? '#c8c8c8' : '#4d4d4d'
          } else if (rate > 0) {
            style.color = props.theme === 'dark' ? '#ff5c5c' : '#cc2b1d'
          } else {
            // rate < 0
            style.color = props.theme === 'dark' ? '#39e365' : '#439731'
          }
        }
      }
      // 盟主趋势最高涨幅：根据maxRate自身的值显示颜色（和四维猎手涨幅一样）
      // >0: 红色; <0: 绿色; =0: 灰色
      if (isMengzhuTrend.value && col.key === 'maxRate') {
        const rate = record.maxRate
        if (rate === null || rate === undefined || rate === 0) {
          style.color = props.theme === 'dark' ? '#c8c8c8' : '#4d4d4d'
        } else if (rate > 0) {
          style.color = props.theme === 'dark' ? '#ff5c5c' : '#cc2b1d'
        } else {
          // rate < 0
          style.color = props.theme === 'dark' ? '#39e365' : '#439731'
        }
      }
      // 盟主趋势累计涨幅：根据cumRate自身的值显示颜色（红涨绿跌黑平，和四维猎手最新价一致）
      // >0: 红色; <0: 绿色; =0: 灰色
      if (isMengzhuTrend.value && col.key === 'cumRate') {
        const rate = record.cumRate
        if (rate === null || rate === undefined || rate === 0) {
          style.color = props.theme === 'dark' ? '#c8c8c8' : '#4d4d4d'
        } else if (rate > 0) {
          style.color = props.theme === 'dark' ? '#ff5c5c' : '#cc2b1d'
        } else {
          // rate < 0
          style.color = props.theme === 'dark' ? '#39e365' : '#439731'
        }
      }
      // 盟主趋势资金：根据fund自身的值显示颜色（红正绿负黑平）
      if (isMengzhuTrend.value && col.key === 'fund') {
        const val = parseFloat(record.fund)
        if (isNaN(val) || val === 0) {
          style.color = props.theme === 'dark' ? '#c8c8c8' : '#4d4d4d'
        } else if (val > 0) {
          style.color = props.theme === 'dark' ? '#ff5c5c' : '#cc2b1d'
        } else {
          // val < 0
          style.color = props.theme === 'dark' ? '#39e365' : '#439731'
        }
      }
      // 盟主趋势特定列使用代码列颜色（和四维猎手的代码列一致）
      // 信号池：入选价、题材板块、离场信号时间
      // 近30天热门案例：入选价、入选日期、离场日期
      if (isMengzhuTrend.value && ['buyPx', 'plateName', 'sellTime', 'buyTime'].includes(col.key)) {
        style.color = props.theme === 'dark' ? '#DFE2EA' : '#4D4D4D'
      }
      // 箱体擒龙总量和换手列使用代码列颜色（和四维猎手的代码列一致）
      if (isBoxDragon.value && ['volume', 'turnoverRatio'].includes(col.key)) {
        style.color = props.theme === 'dark' ? '#DFE2EA' : '#4D4D4D'
      }
      // 龙头猎手：入选价、个股热度、所属板块、板块热度、入选时间 使用代码列颜色
      if (isDragonHunter.value && ['price', 'symbolHot', 'plateName', 'plateHot', 'hunterDateTime'].includes(col.key)) {
        style.color = props.theme === 'dark' ? '#DFE2EA' : '#4D4D4D'
      }
      // 龙头信号：个股热度、所属板块 使用代码列颜色
      if (isDragonSignal.value && ['stockHot', 'dragonPlateName'].includes(col.key)) {
        style.color = props.theme === 'dark' ? '#DFE2EA' : '#4D4D4D'
      }
      // 量化淘金：入选价、题材板块、进场/离场信号时间、入选日期、离场日期 使用代码列颜色
      if (isQuantGold.value && ['price', 'plateName', 'sellTime', 'entryTime', 'coverShortTime', 'shortTime', 'hunterDateTime', 'exitDate'].includes(col.key)) {
        style.color = props.theme === 'dark' ? '#DFE2EA' : '#4D4D4D'
      }
      // 机构分时：机构/散户买卖列固定色（放在末尾，避免被其他规则覆盖）
      if (isAgencyIntraday.value) {
        const agencyColor = getAgencyRateColor(col.key) || col.color
        if (agencyColor) {
          style.color = agencyColor
        }
        // 题材板块与名称列同色
        if (col.key === 'plateName') {
          style.color = props.theme === 'dark' ? '#DFE2EA' : '#333'
        }
        // 量化信号/淘金指无数据时与默认空值同色
        if (col.key === 'goldSignal') {
          const display = record._agencyGoldDisplay
          if (display !== 'trigger' && display !== 'view') {
            style.color = props.theme === 'dark' ? '#DFE2EA' : '#4D4D4D'
          }
        }
      }
      return style
    }

    // 排序处理：首次点击降序↓ → 再次点击升序↑ → 再次点击取消 → 循环
    const handleSort = (field: string) => {
      if (isQuantGold.value && props.quantGoldMainTab === 'hotCase') return
      if (sortField.value === field) {
        if (sortOrder.value === 'descend') {
          sortOrder.value = 'ascend'
        } else if (sortOrder.value === 'ascend') {
          sortField.value = ''
          sortOrder.value = ''
        } else {
          sortOrder.value = 'descend'
        }
      } else {
        sortField.value = field
        sortOrder.value = 'descend'
      }
      emit('change', {
        pagination: null,
        sorter: sortField.value ? { field: sortField.value, order: sortOrder.value } : {}
      })
    }

    // 滚动加载处理
    const handleScroll = (e: Event) => {
      const target = e.target as HTMLElement
      if (!target) return

      // 同步表头水平滚动
      if (tableHeaderRef.value) {
        tableHeaderRef.value.scrollLeft = target.scrollLeft
      }

      const { scrollTop, scrollHeight, clientHeight } = target
      scheduleScrollVisibleEmit()
      if (scrollHeight - scrollTop - clientHeight < 50) {
        if (props.hasMore && !props.loadingMore && !props.loading) {
          emit('load-more')
        }
      }
    }

    // 用于区分单击和双击的定时器
    let clickTimer: ReturnType<typeof setTimeout> | null = null
    const CLICK_DELAY = 250 // 单击延迟时间，用于区分双击

    onUnmounted(() => {
      if (clickTimer) {
        clearTimeout(clickTimer)
        clickTimer = null
      }
      if (scrollVisibleTimer) {
        clearTimeout(scrollVisibleTimer)
        scrollVisibleTimer = null
      }
      if (scrollEndTimer) {
        clearTimeout(scrollEndTimer)
        scrollEndTimer = null
      }
      if (visibleEmitRetryTimer) {
        clearTimeout(visibleEmitRetryTimer)
        visibleEmitRetryTimer = null
      }
      visibleEmitRetryCount = 0
      unbindScrollListener()
    })

    const agencyHasQuantPermission = computed(() => props.agencyHasQuantPermission)

    function isAgencyGoldInteractionTarget(event?: Event) {
      if (!isAgencyIntraday.value || !event) return false
      const target = event.target as HTMLElement | null
      return !!target?.closest('.agency-gold-cell, .agency-gold-header')
    }

    const handleAgencyGoldCellDblClick = (record: PoolData) => {
      if (clickTimer) {
        clearTimeout(clickTimer)
        clickTimer = null
      }
      emit('agency-gold-dblclick', record)
    }

    const handleAgencyGoldHeaderDblClick = () => {
      emit('agency-gold-dblclick', null)
    }

    // 单击行
    const handleRowClick = (record: PoolData, event?: Event) => {
      if (isAgencyGoldInteractionTarget(event)) return
      // isClick=1 弹窗模式：没有单击，直接返回
      if (props.isClick === 1) return

      // 清除之前的定时器（如果有）
      if (clickTimer) {
        clearTimeout(clickTimer)
        clickTimer = null
      }

      // isClick=2 内嵌模式：延迟执行单击，等待判断是否为双击
      clickTimer = setTimeout(() => {
        const code = record.symbol
        const market = record.market || 'sz'
        const url = `gw://funccall?funcname=OnFireStyleEvent;Command=set_focus_stock;param=<params><code>${code}</code><market_letter>${market}</market_letter></params>`
        window.open(url)
        console.log('danji ', url)
        emit('row-click', record)
        clickTimer = null
      }, CLICK_DELAY)
    }

    // 双击行
    const handleRowDblClick = (record: PoolData, event?: Event) => {
      if (isAgencyGoldInteractionTarget(event)) return
      // 取消单击的延迟执行
      if (clickTimer) {
        clearTimeout(clickTimer)
        clickTimer = null
      }

      setTimeout(() => {
        if (isAgencyIntraday.value) {
          openAgencyIndicatorNavigation(record, props.dataSource as PoolData[], props.productData || [])
          emit('row-dblclick', record)
          return
        }

        const code = record.symbol
        const market = record.market || 'sz'
        const clickedIndex = props.dataSource.findIndex((item: PoolData) => item.symbol === record.symbol)
        const afterClicked = props.dataSource.slice(clickedIndex + 1) as PoolData[]
        const beforeClicked = props.dataSource.slice(0, clickedIndex) as PoolData[]
        const reorderedList: PoolData[] = [record].concat(afterClicked).concat(beforeClicked)
        const stockList = reorderedList.map((item: PoolData) => `${item.market || 'sz'}:${item.symbol}`).join(',')

        const url1 = `gw://funccall?funcname=SetCurStockList;list=${stockList}`
        window.open(url1)
        setTimeout(() => {
          const indAlias = (props.productData && props.productData[0]?.poolName) || courseName.value
          const indParam = String(route.query.isMbzb) == '1'
            ? `<ind_alias>${indAlias}</ind_alias>`
            : `<ind_alias>${indAlias}</ind_alias>`
          const url2 = `gw://funccall?funcname=OnFireCommand;setcurrent=1;Command=kline;param=<params><kline_special_event>0</kline_special_event><StyleName>JSFX</StyleName><code>${code}</code><market_letter>${market}</market_letter><periodstr>DAY</periodstr><TabName>K线图</TabName><flag_container_style>K</flag_container_style>${indParam}</params>`
          window.open(url2)
          emit('row-dblclick', record)
        }, 100)
      }, 50)
    }


    // 趋势势能相关
    // 大于0：强势 黄色；小于0：弱势 绿色；等于0：-
    const getMacdColor = (val: number) => {
      if (val > 0) return '#f8a000'
      if (val < 0) return props.theme === 'dark' ? '#39e365' : '#439731'
      return props.theme === 'dark' ? '#c8c8c8' : '#4d4d4d'
    }
    const getMacdText = (val: number) => {
      if (val === null || val === undefined) return '--'
      if (val === 0) return '-'
      const status = val > 0 ? '强势' : '弱势'
      return `${status}(${val.toFixed(2)})`
    }

    // 资金标尺相关
    // 大于0：强势 红色；小于0：弱势 蓝色；等于0：-
    const getAmountColor = (val: number) => {
      if (val > 0) return props.theme === 'dark' ? '#ff5c5c' : '#cc2b1d'
      if (val < 0) return props.theme === 'dark' ? '#00ffff' : '#0079d5'
      return props.theme === 'dark' ? '#c8c8c8' : '#4d4d4d'
    }
    const getAmountText = (val: number) => {
      if (val === null || val === undefined) return '--'
      if (val === 0) return '-'
      const formattedVal = formatAmount(val)
      const status = val > 0 ? '强势' : '弱势'
      return `${status}(${formattedVal})`
    }

    // 盟主趋势成交额格式化（只显示金额，和四维猎手的成交额列一致）
    const getMengzhuAmountText = (val: number) => {
      if (val === null || val === undefined) return '--'
      if (val === 0) return '-'
      // 只显示金额，不显示强势/弱势
      return formatAmount(val)
    }

    // 题材追踪相关
    // 核心：紫色；普通：黄色；未入选：黑色
    const getPlateStyle = (type: string | number) => {
      const typeStr = String(type)
      if (typeStr === '1') return { color: '#883cd1' } // 核心 紫色
      if (typeStr === '2') return { color: '#f8a000' } // 普通 黄色
      return { color: props.theme === 'dark' ? '#c8c8c8' : '#4d4d4d' } // 未入选 黑色
    }
    const getPlateText = (type: string | number, name: string) => {
      const typeStr = String(type)
      const typeStrname = String(name)
      if (typeStr === '1') return `${name}(核心)`
      if (typeStr === '2') return `${name}(普通)`
      if (typeStrname !== '' && typeStr === '0') return `${name}(未入选)`
      return `——`
    }

    // 筹码大师相关
    // 筹码状态颜色
    const getChipStateColorStyle = (state: string, stateColor?: number) => {
      return getChipStateColor(state, stateColor, props.theme as 'light' | 'dark')
    }

    // 筹码形态颜色（固定灰色）
    const getChipShapeColorStyle = () => {
      return getChipShapeColor(props.theme as 'light' | 'dark')
    }

    // 入选时间颜色（与序号相同）
    const getInPoolTimeColorStyle = () => {
      return getInPoolTimeColor(props.theme as 'light' | 'dark')
    }

    // 格式化入选时间
    const formatInPoolTimeValue = (timestamp: number | undefined | null) => {
      return formatInPoolTime(timestamp)
    }

    // 盟主趋势相关
    // 盟主资金格式化（亿）
    const formatMengzhuFund = (val: string | number | undefined | null) => {
      if (val === null || val === undefined || val === '') return '--'
      const num = typeof val === 'string' ? parseFloat(val) : val
      if (isNaN(num)) return '--'

      const str = num.toFixed(2)

      // 等于 0 → 只返回 0.00
      if (num == 0) {
        return '0.00'
      }

      // 大于 0 → 带 亿
      return str + '亿'
    }

    // 格式化盟主趋势日期
    const formatMengzhuDateValue = (timestamp: number | undefined | null) => {
      return formatMengzhuDate(timestamp)
    }

    // 判断盟主趋势最新信号模式下，0值是否应该显示为无效（"-"）
    // 逻辑：对比最后一个有效交易日和信号池的 updateTime 日期
    // 如果日期相同：后端返回的涨跌幅字段为0时，显示为"-"（无效）
    // 如果交易日大于信号池日期（+1天）：涨跌幅字段正常显示，0是有效值，显示为"0%"
    // 历史信号模式：0值始终显示为 "0.00%"（有效值）
    const shouldShowZeroAsInvalid = () => {
      // 只在盟主趋势最新信号模式下生效
      if (!isMengzhuTrend.value) return true
      if (props.mengzhuMainTab !== 'signalPool') return true
      // 历史信号模式：0值是有效值，不显示为无效
      if (props.mengzhuSignalType !== 'latest') return false

      const lastTradingDay = props.mengzhuLastTradingDay
      const updateTimeStr = props.mengzhuUpdateTime

      if (!lastTradingDay || !updateTimeStr) return true

      // 获取 updateTime 的日期部分（YYYY-MM-DD）
      const updateTimeDate = moment(updateTimeStr).format('YYYY-MM-DD')

      // 如果最后一个有效交易日和信号池日期相同，0值显示为无效
      // 如果最后一个有效交易日大于信号池日期，0值是有效值
      return lastTradingDay <= updateTimeDate
    }

    // 格式化盟主趋势最高涨幅
    // 盟主趋势最新信号模式下：
    // - 如果最后一个有效交易日和信号池日期相同：0值显示为"-"（无效）
    // - 如果最后一个有效交易日大于信号池日期：0值是有效值，显示为"0%"
    const formatMengzhuMaxRate = (val: number | undefined | null) => {
      if (val === null || val === undefined) return '-'
      if (val === 0) {
        // 判断0值是否应该显示为无效
        if (shouldShowZeroAsInvalid()) {
          return '-'
        }
        return '0.00%'
      }
      // maxRate 是小数形式（如 0.05 表示 5%），需要乘以100
      const percent = val * 100
      return percent.toFixed(2) + '%'
    }

    // 格式化盟主趋势累计涨幅
    // 信号池：需要乘以100（cumRate 是小数形式，如 0.1694 表示 16.94%）
    // 近30天热门案例：不需要乘以100（cumRate 已经是百分比形式，如 16.94 表示 16.94%）
    // 盟主趋势最新信号模式下：
    // - 如果最后一个有效交易日和信号池日期相同：0值显示为"-"（无效）
    // - 如果最后一个有效交易日大于信号池日期：0值是有效值，显示为"0%"
    const formatMengzhuCumRate = (val: number | undefined | null, mainTab?: string) => {
      if (val === null || val === undefined) return '-'
      if (val === 0) {
        // 近30天热门案例：0值始终显示为"-"
        if (mainTab === 'hotCases') {
          return '-'
        }
        // 信号池：判断0值是否应该显示为无效
        if (shouldShowZeroAsInvalid()) {
          return '-'
        }
        return '0.00%'
      }
      // 近30天热门案例不需要乘以100
      if (mainTab === 'hotCases') {
        return val.toFixed(2) + '%'
      }
      // 信号池需要乘以100
      const percent = val * 100
      return percent.toFixed(2) + '%'
    }

    // 盟主趋势涨跌幅颜色
    const getMengzhuChangeColor = (value: number | undefined | null) => {
      return getChangeColor(value, props.theme as 'light' | 'dark')
    }

    // 量化淘金：最高涨幅 / 累计涨幅（空值、0 与最高涨幅同为灰色默认色）
    const getQuantGoldPctColor = (value: number | undefined | null) => {
      if (value === null || value === undefined || value === 0) {
        return props.theme === 'dark' ? '#c8c8c8' : '#4d4d4d'
      }
      if (value > 0) {
        return props.theme === 'dark' ? '#ff5c5c' : '#cc2b1d'
      }
      return props.theme === 'dark' ? '#39e365' : '#439731'
    }

    const openAiInterpDetail = (record: PoolData) => {
      emit('open-ai-interp', record)
    }

    const getShortReason = (reason: string) => {
      if (!reason) return ''
      return reason.length > 6 ? reason.slice(0, 6) + '...' : reason
    }

    return {
      tableWrapperRef,
      tableBodyRef,
      tableHeaderRef,
      sortField,
      sortOrder,
      columns,
      displayColumns,
      draggableColumns,
      defaultDraggableColumns,
      headerSettingsVisible,
      courseName,
      shouldMask,
      isMengzhuTrend,
      isBoxDragon,
      isDragonSignal,
      currentMengzhuMainTab,
      getDisplayName,
      getDisplayCode,
      getAlignClass,
      getFixedLeft,
      formatCellValue,
      formatLastPx,
      formatAgencyLastPx,
      formatPxChangRate,
      formatSelectRate,
      getCellClass,
      getCellStyle,
      handleSort,
      handleScroll,
      handleRowClick,
      handleRowDblClick,
      openHeaderSettings,
      handleHeaderSettingsApply,
      handleHeaderSettingsCancel,
      getMacdColor,
      getMacdText,
      getAmountColor,
      getAmountText,
      getMengzhuAmountText,
      getPlateStyle,
      getPlateText,
      // 筹码大师相关
      getChipStateColorStyle,
      getChipShapeColorStyle,
      getInPoolTimeColorStyle,
      formatInPoolTimeValue,
      // 盟主趋势相关
      formatMengzhuFund,
      formatMengzhuDateValue,
      formatMengzhuMaxRate,
      formatMengzhuCumRate,
      getMengzhuChangeColor,
      getQuantGoldPctColor,
      isQuantGold,
      isAgencyIntraday,
      agencyHasQuantPermission,
      formatAgencyRate,
      getAgencyRateColor,
      handleAgencyGoldCellDblClick,
      handleAgencyGoldHeaderDblClick,
      openAiInterpDetail,
      getShortReason,
      tableContainerStyle,
      settingsIconHover,
      settingsIconSrc,
      agencyGoldLockSrc,
      isXpOs,
      // 龙头信号相关
      formatStockHot,
      // 龙头猎手相关
      formatUpSpaceStar,
      formatQuantGoldDate,
      formatHunterHot,
      getHunterSelectTypeColor,
      getHunterSelectTypeText,
      formatHunterDate
    }
  }
})
